import { Link } from 'react-router-dom';
import { Icon } from '../../../components/ui/Icon';
import { MenuAnchor } from '../../../components/ui/MenuAnchor';
import { asset } from '../../../lib/asset';
import { brand, footer } from '../../../content/site';
import { useLegalDocs, useMenu, useSiteInfo } from '../../../hooks/useCms';

const contactIcon = 'flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] border border-blue-400/[0.18] bg-blue-500/[0.08] text-blue-400';

export default function Footer() {
  const site = useSiteInfo();
  // Admin'de yayından kaldırılan yasal metin footer'da da görünmez.
  const { hiddenHrefs } = useLegalDocs();
  // Bağlantı sütunları admin → Menü → Footer'dan gelir.
  const { footerColumns } = useMenu();

  return (
    // Alt boşluk: telefonda Z'eki hapı tam genişlikte (alt 16px + 48px) olduğu için son satırı örtmesin diye 64px kalır. Masaüstünde
    // hap ortada 360px'lik bir kapsül; solda telif, sağda "Yasal Merkez" bağlantısı hapın iki yanında kaldığından metnin
    // hapı aşması gerekmez → yalnız `lg:pb-6`.
    <footer id="iletisim" className="relative overflow-hidden bg-surface-deep pb-[64px] lg:pb-6">
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-px bg-[linear-gradient(90deg,rgba(69,192,238,0),rgba(69,192,238,0.55),rgba(69,192,238,0))]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-[220px] left-1/2 h-[440px] w-[900px] -translate-x-1/2"
        style={{ background: 'radial-gradient(closest-side, rgba(69,192,238,0.12), rgba(0,0,0,0))' }}
      />

      <div className="relative mx-auto max-w-[1200px] px-4 pb-8 pt-16 sm:px-6 lg:pt-20">
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-[minmax(0,1.45fr)_minmax(0,0.75fr)_minmax(0,1.05fr)_minmax(0,0.75fr)_minmax(0,1.25fr)]">
          <div className="flex flex-col gap-[22px] sm:col-span-2 lg:col-span-1">
            <Link to="/" aria-label={`${brand.name} ana sayfa`} className="self-start">
              {/* Kenar boşluğu kırpılmış logo: açıklama ve iletişim satırlarıyla aynı çizgiden başlar. */}
              <img src={asset('assets/z-hesap-logo-dark-tight.svg')} width={195} height={53} alt={brand.name} className="block h-[34px] w-auto" />
            </Link>
            <p className="m-0 max-w-[300px] text-[15px] leading-[1.65] text-ink-soft">{site.tagline}</p>
            <ul className="m-0 flex list-none gap-2.5 p-0">
              {site.socials.map((social) => (
                <li key={social.label}>
                  <a
                    href={social.href}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    aria-label={social.label}
                    className="ft-soc no-underline"
                  >
                    <Icon name={social.icon} size={18} />
                  </a>
                </li>
              ))}
            </ul>

            {/* İletişim künyesi: e-posta, telefon, adres. */}
            <address className="mt-1.5 flex flex-col gap-3.5 not-italic">
              <a href={`mailto:${site.email}`} className="flex items-start gap-3 text-[14px] leading-[1.5] text-[#c3d0e6] no-underline hover:text-white">
                <span className={contactIcon}>
                  <Icon name="mail" size={16} />
                </span>
                <span>
                  <span className="block text-[12px] text-ink-quiet">{footer.contact.email}</span>
                  {site.email}
                </span>
              </a>
              <a href={site.phoneHref} className="flex items-start gap-3 text-[14px] leading-[1.5] text-[#c3d0e6] no-underline hover:text-white">
                <span className={contactIcon}>
                  <Icon name="phone" size={16} />
                </span>
                <span>
                  <span className="block text-[12px] text-ink-quiet">{footer.contact.phone}</span>
                  {site.phone}
                </span>
              </a>
              <div className="flex items-start gap-3 text-[14px] leading-[1.5] text-[#c3d0e6]">
                <span className={contactIcon}>
                  <Icon name="pin" size={16} />
                </span>
                <span>
                  <span className="block text-[12px] text-ink-quiet">{footer.contact.address}</span>
                  {site.address}
                  <span className="mt-0.5 block text-[13px] leading-[1.55] text-ink-subdued">{site.addressDetail}</span>
                </span>
              </div>
            </address>
          </div>

          {footerColumns.map((col, c) => (
            <nav key={`${c}-${col.title}`} aria-label={col.title} className="flex flex-col">
              <h2 className="m-0 mb-4 text-[12px] font-bold uppercase tracking-[0.16em] text-ink-quiet">{col.title}</h2>
              <ul className="m-0 list-none p-0">
                {col.links.filter((link) => !hiddenHrefs.includes(link.href)).map((link, l) => (
                  <li key={`${l}-${link.href}`}>
                    <MenuAnchor href={link.href} className="ft-l no-underline">
                      {link.label}
                    </MenuAnchor>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>

        {/* Güven etiketleri menü bölümünün parçası: masaüstünde 4 eşit sütun, tek satır. */}
        <ul className="m-0 mt-12 grid list-none grid-cols-1 gap-2.5 p-0 sm:grid-cols-2 lg:mt-14 lg:grid-cols-4">
          {footer.trust.map((t) => (
            <li
              key={t.label}
              className="flex items-center justify-center gap-2 rounded-full border border-blue-400/[0.16] bg-white/[0.035] px-3.5 py-2.5 text-center text-[12.5px] font-semibold text-[#c3d0e6]"
            >
              <Icon name={t.icon} size={16} className="shrink-0 text-blue-400" />
              {t.label}
            </li>
          ))}
        </ul>
      </div>

      <div className="relative mx-auto max-w-[1200px] px-4 sm:px-6">
        <div className="flex flex-col gap-3 border-t border-white/[0.07] pb-3 pt-5 text-[13px] leading-[1.5] text-ink-quiet sm:flex-row sm:items-center sm:justify-between">
          <span>{site.copyright}</span>
          <a
            href={footer.legalHub.href}
            className="py-1.5 text-ink-soft underline decoration-[#33405a] underline-offset-[3px] hover:text-white"
          >
            {footer.legalHub.label}
          </a>
        </div>
      </div>
    </footer>
  );
}
